<script>
import { mapGetters } from 'vuex'
import DatePicker from 'vue2-datepicker';
import moment from "moment"
import { required, email } from 'vuelidate/lib/validators'

import Tooltip from '@components/tool-tip.vue'

import Translatable from '@src/mixins/translatable.js'

export default {
  components: {
    DatePicker,
    Tooltip
  },
  mixins: [
    Translatable
  ],
  validations: {
    arrivalDay: {
      required,
    },
    name: {
      required,
    },
    email: {
      email,
      required
    },
    country: {
      required
    },
  },
  data() {
    return {
      arrivalDay: new Date(),
      momentFormat: {
        stringify: (date) => {
          return date ? moment(date).format("DD/MM/YYYY") : ''
        },
      },
      name: '',
      email: '',
      country: '',
      isOutside: false
    }
  },
  computed: {
    ...mapGetters('eShop', [
      'userData',
      'isFieldsValid',
      'pageData'
    ]),
    lang() {
      return ({
        formatLocale: {
          monthsShort: [
            this.getTranslatedContent('CALENDAR_January'),
            this.getTranslatedContent('CALENDAR_February'),
            this.getTranslatedContent('CALENDAR_March'),
            this.getTranslatedContent('CALENDAR_April'),
            this.getTranslatedContent('CALENDAR_May'),
            this.getTranslatedContent('CALENDAR_June'),
            this.getTranslatedContent('CALENDAR_July'),
            this.getTranslatedContent('CALENDAR_August'),
            this.getTranslatedContent('CALENDAR_September'),
            this.getTranslatedContent('CALENDAR_October'),
            this.getTranslatedContent('CALENDAR_November'),
            this.getTranslatedContent('CALENDAR_December'),
          ],
          weekdaysMin: [
            this.getTranslatedContent('CALENDAR_day_Sunday'),
            this.getTranslatedContent('CALENDAR_day_Monday'),
            this.getTranslatedContent('CALENDAR_day_Tuesday'),
            this.getTranslatedContent('CALENDAR_day_Wednesday'),
            this.getTranslatedContent('CALENDAR_day_Thursday'),
            this.getTranslatedContent('CALENDAR_day_Friday'),
            this.getTranslatedContent('CALENDAR_day_Saturday'),
          ],
        }
      })
    }
  },
  watch: {
    'arrivalDay': function () {
      this.updateField('arrivalDay', this.arrivalDay)
    },
    'name': function () {
      this.updateField('name', this.name)
    },
    'email': function () {
      this.updateField('email', this.email)
    },
    'country': function () {
      this.updateField('country', this.country)
    },
  },
  mounted() {
    if (this.pageData.startDate) {
      this.setArrivalDay()
      return
    }

    this.$store.subscribe(mutation => {
      if (mutation.type === 'eShop/SET_PAGE_DATA') {
        this.$nextTick(() => {
          this.setArrivalDay()
        })
      }
    })
  },
  methods: {
    updateField(field, value) {
      this.$store.commit('eShop/updateUserData', {[field]: value});
      this.$v[field].$touch()
      if (!this.$v.$invalid) {
        this.$store.commit('eShop/changeValid', true);
      }
      if (this.$v.$invalid && this.isFieldsValid) {
        this.$store.commit('eShop/changeValid', false);
      }
    },
    handleFocusOut() {
      this.isOutside = true
    },
    disableDate(date) {
      return date < moment(new Date()).subtract(1, 'd') || date < moment(this.pageData.startDate) || date > moment(this.pageData.endDate)
    },
    setArrivalDay() {
      if (moment(new Date()) < moment(this.pageData.startDate)) {
        this.updateField('arrivalDay', moment(this.pageData.startDate))
        this.arrivalDay = moment(this.pageData.startDate).toDate()
      }
    }
  },
}
</script>

<template>
  <div class="user-form">
    <div class="input-container">
      <label for="date" class="input-title">{{ getTranslatedContent('ESHOP_form_date_field') }}
        <Tooltip
            :text="getTranslatedContent('ESHOP_PickupDateHint')"
          >
            <div class="info-icon"></div>
        </Tooltip>
        <p v-if="$v.arrivalDay.$error && !$v.arrivalDay.required" class="error-message">{{ getTranslatedContent('FORM_validate_required') }}</p>
        <DatePicker
          v-model="arrivalDay"
          type="date"
          :formatter="momentFormat"
          prefix-class="xmx"
          :editable="false"
          :clearable="false"
          :class="$v.arrivalDay.$error ? 'error-d' : ''"
          :disabled-date="disableDate"
          :lang="lang"
        />
      </label>
    </div>
    <div class="input-container">
      <label for="name" class="input-title">{{ getTranslatedContent('REVIEWS_form_name_field') }}
        <Tooltip
            :text="getTranslatedContent('ESHOP_CustomerNameHint')"
          >
            <div class="info-icon"></div>
        </Tooltip>
        <p v-if="$v.name.$error && !$v.name.required" class="error-message">{{ getTranslatedContent('FORM_validate_required') }}</p>
        <input
          id="name"
          v-model="name"
          type="text"
          :placeholder="getTranslatedContent('REVIEWS_form_name_placeholder')"
          :class="$v.name.$error ? 'error' : ''"
        >
      </label>
    </div>
    <div class="input-container">
      <label for="email" class="input-title">{{ getTranslatedContent('REVIEWS_review_form_email_field') }}
        <p v-if="$v.email.$error && !$v.email.required" class="error-message">{{ getTranslatedContent('FORM_validate_required') }}</p>
        <p v-if="!$v.email.email && isOutside" class="error-message">{{ getTranslatedContent('FORM_validate_email') }}</p>
        <input
          id="email"
          v-model="email"
          type="email"
          @focusout="handleFocusOut"
          :placeholder="getTranslatedContent('REVIEWS_form_email_placeholder')"
          :class="$v.email.$error && isOutside ? 'error' : ''"
        >
    </label>
    </div>
    <div class="input-container">
      <label for="city" class="input-title">{{ getTranslatedContent('REVIEWS_review_form_city_field') }}
        <p v-if="$v.country.$error && !$v.country.required" class="error-message">{{ getTranslatedContent('FORM_validate_required') }}</p>
        <input
          id="city"
          v-model="country"
          type="text"
          :placeholder="getTranslatedContent('REVIEWS_form_country_placeholder')"
          :class="$v.country.$error ? 'error' : ''"
        >
      </label>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';
@import '@styles/form.scss';

.info-icon {
  cursor: pointer;
  display: inline-block;
  position: relative;
  min-width: 24px;
  width: 24px;
  height: 24px;
  background: url("../../assets/icons/e-info.svg") center no-repeat;
  bottom: 5px;
  margin: 0 0 -11px 12px;
}

</style>
